/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Authentication Operations
 * 
 * Handles authentication checks and user info retrieval.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import { getSkillShopServerBaseUrl } from './base';

export interface UserInfo {
  id: number;
  organizationId: string;
  name: string;
  email: string;
}

/**
 * Get the current authenticated user's info
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getCurrentUser(): Promise<UserInfo | null> {
  try {
    const response = await authenticatedFetch('/api/auth/me');
    return await handleApiResponse<UserInfo>(response);
  } catch (error) {
    console.error('[SkillShop Service] Error fetching current user:', error);
    return null;
  }
}

/**
 * Test connection to SkillShop server
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function testConnection(): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await authenticatedFetch('/api/health');

    if (!response.ok) {
      throw new Error(`Connection test failed (${response.status}): ${response.statusText}`);
    }

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Connection failed'
    };
  }
}

/**
 * Check authentication status (for Electron mode)
 * SkillShop server endpoint - uses direct fetch (no auth headers)
 */
export async function checkElectronAuth(): Promise<{ 
  success: boolean; 
  user?: UserInfo; 
  error?: string 
}> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/auth/me`);
    
    if (response.ok) {
      const result = await response.json();
      if (result?.success && result.data) {
        return { success: true, user: result.data };
      }
    }
    
    return { success: false, error: 'No logged-in user found' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Unable to verify access' };
  }
}

